"use client";

import { useSession } from "@/lib/auth/client";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { PhoneInput } from "@/components/app-ui/phone-input";
import { LevelSelect } from "@/components/level-select";
import { Button } from "@/components/shadcn/button";
import { Checkbox } from "@/components/shadcn/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/shadcn/dialog";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import type { SocialLevel } from "@/lib/social-ratings/levels";
import { validatePhoneNumber } from "@/lib/utils";

// One-time post-signup onboarding, multi-step: intro → name → phone → ELO →
// mail consents. Deliberately NOT auto-opening a modal — this mounts in the
// root layout and the account check is async, so an eager dialog could cover
// any page at an arbitrary moment (including the premium purchase flow). A
// toast invites; the wizard only opens if the user chooses to. Steps only
// appear for data the account is missing; checkout still collects name/phone
// itself when needed. "Daha sonra" and ESC dismiss per-device (localStorage).
const DISMISS_KEY = "name-onboarding-dismissed";
const INVITE_TITLE = "Sizi tanıyalım 👋"; // i18n-ignore: extract with #802

type Step = "intro" | "name" | "phone" | "level" | "consent";

export default function NameOnboardingDialog() {
    // better-auth useSession has no `status`; derive a stable boolean so the
    // effect below does not refire on every session object identity change.
    const { data: session } = useSession();
    const authed = !!session?.user;
    const [open, setOpen] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    const [stepIdx, setStepIdx] = useState(0);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [level, setLevel] = useState<SocialLevel | null>(null);
    // Two consents, ONE stored flag: pazarlama and duyuru both live in
    // UserAccount.Advertisement until the better-auth migration gives us
    // per-user metadata to split them. Either checked → mails allowed.
    const [marketingOk, setMarketingOk] = useState(false);
    const [announceOk, setAnnounceOk] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!authed) return;
        if (localStorage.getItem(DISMISS_KEY)) return;
        fetch("/api/user/details")
            .then((r) => (r.ok ? r.json() : null))
            .then((u) => {
                if (!u) return;
                const needsName = !u.name?.trim() || !u.surname?.trim();
                const needsPhone = !u.phone?.trim();
                // Only invite when purchase-critical data is missing; ELO and
                // consents alone aren't worth a nag — they just ride along.
                if (!needsName && !needsPhone) return;

                const s: Step[] = ["intro"];
                if (needsName) s.push("name");
                if (needsPhone) s.push("phone");
                if (u.eloRating == null) s.push("level");
                s.push("consent");
                setSteps(s);
                const already = u.advertisement === 1;
                setMarketingOk(already);
                setAnnounceOk(already);

                // i18n-ignore markers: prototype copy, extract with #802
                toast(INVITE_TITLE, {
                    description:
                        "Birkaç soruyla hesabınızı tamamlayalım — üyelik satın alırken işinizi kolaylaştırır.", // i18n-ignore: extract with #802
                    duration: 12000,
                    action: {
                        label: "Başlayalım", // i18n-ignore: extract with #802
                        onClick: () => setOpen(true),
                    },
                    cancel: {
                        label: "Daha sonra",
                        onClick: () => localStorage.setItem(DISMISS_KEY, "1"),
                    },
                });
            })
            .catch(() => {});
    }, [authed]);

    const step = steps[stepIdx];
    const isLast = stepIdx === steps.length - 1;

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, "1");
        setOpen(false);
    };

    const stepValid = () => {
        switch (step) {
            case "name":
                return !!firstName.trim() && !!lastName.trim();
            case "phone":
                // optional — but a half-typed number can't advance
                return !phone.trim() || validatePhoneNumber(phone.trim());
            case "level":
                // optional — skippable, asked again on first event join
                return true;
            default:
                return true;
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await fetch("/api/user/save-name", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...(steps.includes("name") && {
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                    }),
                    ...(phone.trim() && { phone: phone.trim() }),
                    emailConsent: marketingOk || announceOk,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || "Kaydedilemedi.");
            if (level) {
                // Best-effort: the level is re-asked at first event join.
                await fetch("/api/user/social-level", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ level }),
                }).catch(() => undefined);
            }
            localStorage.setItem(DISMISS_KEY, "1");
            toast.success("Teşekkürler! Bilgileriniz kaydedildi."); // i18n-ignore: extract with #802
            setOpen(false);
        } catch (e) {
            toast.error(
                e instanceof Error
                    ? e.message
                    : "Kaydedilemedi, tekrar deneyin.",
            );
        } finally {
            setSaving(false);
        }
    };

    const next = () => {
        if (isLast) {
            void save();
        } else {
            setStepIdx((i) => i + 1);
        }
    };

    return (
        // Closing via ESC/overlay counts as "Daha sonra" — never nag on every page load.
        <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {
                            step === "intro" &&
                                "Sizi tanıyalım 👋" /* i18n-ignore */
                        }
                        {
                            step === "name" &&
                                "İsminiz, soyisminiz" /* i18n-ignore */
                        }
                        {
                            step === "phone" &&
                                "Telefon numaranız" /* i18n-ignore */
                        }
                        {
                            step === "level" &&
                                "Satranç seviyeniz" /* i18n-ignore: extract with #802 */
                        }
                        {step === "consent" && "Sizi haberdar edelim mi?"}
                    </DialogTitle>
                    <DialogDescription>
                        {
                            step === "intro" &&
                                "Birkaç kısa adımda hesabınızı tamamlayalım. İstediğiniz adımı atlayabilirsiniz." /* i18n-ignore */
                        }
                        {
                            step === "name" &&
                                "Üyelik satın alırken ihtiyacımız olacak — faturanızda bu isim görünür." /* i18n-ignore */
                        }
                        {
                            step === "phone" &&
                                "Üyelik satın alırken kullanmak için. Dilerseniz boş bırakın." /* i18n-ignore */
                        }
                        {
                            step === "level" &&
                                "Biliyorsanız ELO puanınızı ekleyin; size uygun içerik önermemize yardımcı olur." /* i18n-ignore */
                        }
                        {
                            step === "consent" &&
                                "Tercihlerinizi istediğiniz zaman profilinizden değiştirebilirsiniz." /* i18n-ignore */
                        }
                    </DialogDescription>
                </DialogHeader>

                {step === "name" && (
                    <div className="flex gap-2">
                        <Input
                            placeholder={"Adınız" /* i18n-ignore */}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        <Input
                            placeholder={"Soyadınız" /* i18n-ignore */}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                )}

                {step === "phone" && (
                    // International: every stored phone is E.164 and goes to
                    // iyzico as gsmNumber, so the country code is required.
                    <PhoneInput
                        international
                        defaultCountry="TR"
                        placeholder={"Telefon (isteğe bağlı)" /* i18n-ignore */}
                        value={phone}
                        onChange={(v) => setPhone(v ?? "")}
                    />
                )}

                {step === "level" && (
                    <LevelSelect value={level} onChange={setLevel} />
                )}

                {step === "consent" && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="consent-marketing"
                                checked={marketingOk}
                                onCheckedChange={(v) => setMarketingOk(!!v)}
                            />
                            <Label
                                htmlFor="consent-marketing"
                                className="text-sm leading-snug font-normal"
                            >
                                {
                                    "Kampanya, indirim ve yeni içeriklerden e-posta ile haberdar olmak isterim." /* i18n-ignore: extract with #802 */
                                }
                            </Label>
                        </div>
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="consent-announce"
                                checked={announceOk}
                                onCheckedChange={(v) => setAnnounceOk(!!v)}
                            />
                            <Label
                                htmlFor="consent-announce"
                                className="text-sm leading-snug font-normal"
                            >
                                {
                                    "Satranç365'teki yenilik ve duyurulardan haberdar olmak isterim." /* i18n-ignore: extract with #802 */
                                }
                            </Label>
                        </div>
                    </div>
                )}

                {/* progress dots */}
                <div className="flex justify-center gap-1.5">
                    {steps.map((s, i) => (
                        <span
                            key={s}
                            className={`h-1.5 w-1.5 rounded-full ${
                                i <= stepIdx ? "bg-primary" : "bg-neutral-200"
                            }`}
                        />
                    ))}
                </div>

                <DialogFooter className="gap-2 sm:justify-between">
                    {stepIdx === 0 ? (
                        <Button variant="ghost" onClick={dismiss}>
                            Daha sonra
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            onClick={() => setStepIdx((i) => i - 1)}
                            disabled={saving}
                        >
                            Geri
                        </Button>
                    )}
                    <Button onClick={next} disabled={saving || !stepValid()}>
                        {saving
                            ? "Kaydediliyor…"
                            : step === "intro"
                              ? "Başlayalım" // i18n-ignore: extract with #802
                              : isLast
                                ? "Kaydet"
                                : "Devam"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
